import { useState } from "react"
import { AcademicianButton } from "@/components/academician/academician-button"
import { PlusIcon, SearchIcon } from "lucide-react"
import { Input } from "@/components/ui/input"
import {
  CohortTableRow,
  Cohort,
  CohortStatus,
} from "@/components/academician/cohort-table-row"
import {
  CohortFormModal,
  CohortFormData,
} from "@/components/academician/cohort-form-modal"
import { cn } from "@/lib/utils"
import { apiPost } from "@/lib/api"

interface ManageCohortsPageProps {
  /**
   * Initial cohorts data
   */
  cohorts?: Cohort[]

  /**
   * Available modules for assignment
   */
  availableModules?: Array<{ id: string; name: string }>

  /**
   * Create cohort callback
   */
  onCreate?: (data: CohortFormData) => void

  /**
   * Update cohort callback
   */
  onUpdate?: (id: string, data: CohortFormData) => void

  /**
   * Archive cohort callback
   */
  onArchive?: (id: string) => void

  /**
   * Additional CSS classes
   */
  className?: string
}

type FilterStatus = "all" | CohortStatus

export function ManageCohortsPage({
  cohorts: initialCohorts = [],
  availableModules = [],
  onCreate,
  onUpdate,
  onArchive,
  className,
}: ManageCohortsPageProps) {
  const [cohorts, setCohorts] = useState(initialCohorts)
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("all")
  const [searchQuery, setSearchQuery] = useState("")
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingCohort, setEditingCohort] = useState<CohortFormData | undefined>()

  // Filter cohorts
  const filteredCohorts = cohorts.filter((cohort) => {
    const matchesStatus =
      filterStatus === "all" || cohort.status === filterStatus
    const matchesSearch = cohort.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
    return matchesStatus && matchesSearch
  })

  // Calculate stats
  const stats = {
    total: cohorts.length,
    enrolling: cohorts.filter((c) => c.status === "enrolling").length,
    inProgress: cohorts.filter((c) => c.status === "in-progress").length,
    completed: cohorts.filter((c) => c.status === "completed").length,
    archived: cohorts.filter((c) => c.status === "archived").length,
    totalRevenue: cohorts.reduce((sum, c) => sum + c.revenue, 0),
    totalEnrolled: cohorts.reduce((sum, c) => sum + c.enrolled, 0),
  }

  const handleCreateCohort = () => {
    window.location.href = "/admin/cohorts/new"
  }

  const handleEditCohort = (cohort: Cohort) => {
    const slug = cohort.slug || cohort.id
    window.location.href = `/admin/cohorts/${slug}/edit`
  }

  const handleViewCohort = (cohort: Cohort) => {
    const slug = cohort.slug || cohort.id
    window.location.href = `/admin/cohorts/${slug}`
  }

  const handleArchiveCohort = (cohort: Cohort) => {
    if (confirm(`Are you sure you want to archive "${cohort.name}"?`)) {
      setCohorts(
        cohorts.map((c) =>
          c.id === cohort.id ? { ...c, status: "archived" as CohortStatus } : c
        )
      )
      onArchive?.(cohort.id)
    }
  }

  const handleSaveCohort = async (data: CohortFormData) => {
    const railsData = {
      cohort: {
        name: data.name,
        objective: data.description,
        description: data.description,
        start_date: data.startDate,
        end_date: data.endDate,
        enrollment_closes_at: data.enrollmentDeadline,
        price_cents: Math.round(data.price * 100),
        max_capacity: data.capacity,
        status: data.status.replace("-", "_"),
        banner_url: data.bannerImage?.startsWith("http") ? data.bannerImage : null,
      },
    }

    try {
      const response = await apiPost("/admin/cohorts.json", railsData)
      if (response.ok) {
        window.location.reload()
      } else {
        let messages = "Failed to create cohort"
        try {
          const result = await response.json()
          if (result.errors) messages = result.errors.join("\n")
        } catch {
          // Response wasn't JSON (e.g. Rails HTML error page)
        }
        alert(messages)
      }
    } catch (err) {
      alert("Network error — please try again")
    }
  }

  const filterButtons: Array<{ label: string; value: FilterStatus; count: number }> = [
    { label: "All", value: "all", count: stats.total },
    { label: "Enrolling", value: "enrolling", count: stats.enrolling },
    { label: "In Progress", value: "in-progress", count: stats.inProgress },
    { label: "Completed", value: "completed", count: stats.completed },
    { label: "Archived", value: "archived", count: stats.archived },
  ]

  return (
    <div className={cn("min-h-screen bg-background", className)}>
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="container mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                Manage Cohorts
              </h1>
              <p className="text-muted-foreground mt-1">
                Create and manage your learning cohorts
              </p>
            </div>
            <AcademicianButton
              variant="primary"
              leftIcon={<PlusIcon className="h-4 w-4" />}
              onClick={handleCreateCohort}
            >
              Create New Cohort
            </AcademicianButton>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="text-sm text-muted-foreground mb-1">
              Total Cohorts
            </div>
            <div className="text-3xl font-bold text-foreground">
              {stats.total}
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="text-sm text-muted-foreground mb-1">
              Active Cohorts
            </div>
            <div className="text-3xl font-bold text-foreground">
              {stats.enrolling + stats.inProgress}
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="text-sm text-muted-foreground mb-1">
              Total Enrolled
            </div>
            <div className="text-3xl font-bold text-foreground">
              {stats.totalEnrolled}
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="text-sm text-muted-foreground mb-1">
              Total Revenue
            </div>
            <div className="text-3xl font-bold text-foreground">
              ${stats.totalRevenue.toLocaleString()}
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-card border border-border rounded-lg p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Status Filters */}
            <div className="flex flex-wrap gap-2">
              {filterButtons.map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => setFilterStatus(filter.value)}
                  className={cn(
                    "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                    filterStatus === filter.value
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  )}
                >
                  {filter.label} ({filter.count})
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative flex-1 lg:max-w-xs ml-auto">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search cohorts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>

        {/* Cohorts Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          {filteredCohorts.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-muted-foreground mb-4">
                {searchQuery
                  ? "No cohorts found matching your search"
                  : "No cohorts found"}
              </div>
              {!searchQuery && (
                <AcademicianButton
                  variant="primary"
                  onClick={handleCreateCohort}
                  leftIcon={<PlusIcon className="h-4 w-4" />}
                >
                  Create Your First Cohort
                </AcademicianButton>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Cohort Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Start Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      End Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Enrolled / Capacity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Revenue
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-card">
                  {filteredCohorts.map((cohort) => (
                    <CohortTableRow
                      key={cohort.id}
                      cohort={cohort}
                      onEdit={handleEditCohort}
                      onView={handleViewCohort}
                      onArchive={handleArchiveCohort}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Results Summary */}
        {filteredCohorts.length > 0 && (
          <div className="mt-4 text-sm text-muted-foreground text-center">
            Showing {filteredCohorts.length} of {cohorts.length} cohorts
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <CohortFormModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        cohort={editingCohort}
        availableModules={availableModules}
        onSave={handleSaveCohort}
        onCancel={() => setIsModalOpen(false)}
      />
    </div>
  )
}
